import Ajv from 'ajv';
import AjvErrors from 'ajv-errors';

import type { CustomNodeData } from '@src/components/custom-nodes/types';
import { nodeDefinitions } from '@src/scenario-editor/default-items-definition.ts';

import type { ZodTypeAny } from 'zod';
import { getBehaviorRulesSchema } from '@src/scenario-editor/validator/schemas/behavior-rules.ts';
import { getIFStatementSchema } from '@src/scenario-editor/validator/schemas/if-statement';
import { getSliderSchema } from '@src/scenario-editor/validator/schemas/slider';

const ajv = AjvErrors(new Ajv({ allErrors: true }));

type NodeDefinitionsKeys = keyof typeof nodeDefinitions;

export interface NodeDefinitionItem {
  validationSchema?: Record<string, unknown>;
  dynamicValidationParams?: Record<string, string[]>;
}

export class Validator {
  private zodRules = new Map<string, ZodTypeAny>();
  private ajvRules = new Map<string, unknown>();
  private fetchContext: Record<string, unknown> = {};
  private dynamicValidationParams: Record<string, Record<string, string[]>> = {};

  setFetchContext(contextName: string, fetchContext: Record<string, unknown>) {
    this.fetchContext[contextName] = fetchContext;
  }

  updateDynamicParamsForSchema(schemaName: string, params: Record<string, unknown[]>) {
    const schema = this.ajvRules.get(schemaName);
    const dynamicParamsForSchema = this.dynamicValidationParams[schemaName];
    if (!schema) {
      throw new Error(`No schema found for ${schemaName}`);
    }

    Object.entries(params).forEach(([key, value]) => {
      const correspondingDynamicParam = dynamicParamsForSchema[key];
      if (!correspondingDynamicParam) {
        throw new Error(`No dynamic param found for ${key}`);
      }
      correspondingDynamicParam.forEach((param) => {
        const paramPath = param.split('.');
        let current = schema as Record<string, unknown>;

        for (let idx = 0; idx < paramPath.length; idx++) {
          const paramPart = paramPath[idx];

          if (idx === paramPath.length - 1) {
            const shouldRemoveParam =
              value === undefined
              || value === null
              || (Array.isArray(value) && value.length === 0);

            if (shouldRemoveParam) {
              delete current[paramPart];
            } else {
              current[paramPart] = value;
            }
          } else {
            if (!current[paramPart] || typeof current[paramPart] !== 'object') {
              current[paramPart] = {};
            }
            current = current[paramPart] as Record<string, unknown>;
          }
        }
      });
    });

    this.ajvRules.set(schemaName, schema);
    // remove ajv cache and re-add with updated schema
    ajv.removeSchema(schemaName);
    ajv.addSchema(schema, schemaName);
  }

  addZodRule(name: string, schema: ZodTypeAny) {
    this.zodRules.delete(name);
    this.zodRules.set(name, schema);
  }

  addAjvRule(name: string, schema: Record<string, unknown>) {
    this.ajvRules.delete(name);
    // Deep clone the schema to prevent mutations affecting the original
    const schemaClone = JSON.parse(JSON.stringify(schema));

    // Remove empty enum arrays to prevent AJV validation errors
    this.removeEmptyEnums(schemaClone);

    this.ajvRules.set(name, schemaClone);
    ajv.removeSchema(name);
    ajv.addSchema(schemaClone, name);
  }

  private removeEmptyEnums(obj: Record<string, unknown>): void {
    for (const key in obj) {
      const value = obj[key];
      if (key === 'enum' && Array.isArray(value) && value.length === 0) {
        delete obj[key];
      } else if (typeof value === 'object' && value !== null) {
        this.removeEmptyEnums(value as Record<string, unknown>);
      }
    }
  }

  addDynamicValidationParams(name: string, params: Record<string, string[]>) {
    this.dynamicValidationParams[name] = params;
  }

  getSchema(scnemaName: string) {
    return this.ajvRules.get(scnemaName);
  }

  isRuleImplemented(name: string) {
    if (!name) {
      return false;
    }
    const namePattern = name.split(':').map((part, idx) => idx === 0 ? part : '*').join(':');
    // check if name included in rules or has rule with '...:*' pattern
    return (
      this.ajvRules.has(name)
      || this.zodRules.has(name)
      || this.ajvRules.has(namePattern)
      || this.zodRules.has(namePattern)
    );
  }

  validate(name: string, value: Partial<CustomNodeData>) {
    const namePattern = name.split(':').map((part, idx) => idx === 0 ? part : '*').join(':');

    const exactAjvSchema = this.ajvRules.get(name);
    const exactZodSchema = this.zodRules.get(name);
    // An exact-name rule must win over a wildcard (e.g. `message:*`) fallback. In particular,
    // a node with an exact Zod rule (message:slider) must not be shadowed by the wildcard AJV
    // rule, since the AJV branch below is evaluated before the Zod branch.
    const ajvSchema = exactAjvSchema ?? (exactZodSchema ? undefined : this.ajvRules.get(namePattern));
    const zodSchema = exactZodSchema ?? this.zodRules.get(namePattern);

    if (!name) {
      return null;
    }

    if (!ajvSchema && !zodSchema) {
      throw new Error(`No validation schema found for ${name}`);
    }

    if (ajvSchema) {
      let res;
      if (ajv.getSchema(name)) {
        res = ajv.validate(name, value);
      } else {
        res = ajv.validate(ajvSchema, value);
      }

      return { success: res, errors: ajv.errors };
    }

    if (zodSchema) {
      const result = zodSchema.safeParse(value);
      // debugger;
      return { success: result.success, errors: (result as { error: unknown }).error as never };
    }

    return null;
  }
}

const validatorInstance = new Validator();


export const initInstanceWithDefinitions = (definitions: Record<string, NodeDefinitionItem>) => {
  Object.keys(definitions).forEach((def) => {
    const nodeDef = definitions[def as NodeDefinitionsKeys];
    const schema = nodeDef.validationSchema;
    const dynamicValidationParams = nodeDef?.dynamicValidationParams;
    if (schema) {
      validatorInstance.addAjvRule(def, schema);
    }
    if (dynamicValidationParams) {
      validatorInstance.addDynamicValidationParams(def, dynamicValidationParams);
    }
    return def;
  });
}

initInstanceWithDefinitions(nodeDefinitions as Record<string, NodeDefinitionItem>);

// INFO: special rules as they're not appeared from constructor
validatorInstance.addZodRule('if:condition-behavior', getBehaviorRulesSchema({ possibleQuestions: [], possibleAnswers: [] }));
validatorInstance.addZodRule('if:condition', getIFStatementSchema({  possibleQuestions: [] }));
validatorInstance.addZodRule('message:slider', getSliderSchema());

export { validatorInstance };
